import { useParams } from 'react-router-dom';
import { useClientId } from '../hooks/useClientId';
import { APP_NAME_PATTERN } from '../utils/customApp';
import { resolveAppConfig } from '../utils/resolveAppConfig';
import { useAppRegistry } from '../api/endpoints/useAppRegistry';
import LoadingSpinner from './LoadingSpinner';
import { CustomAppRemoteContainer } from '../customApps/CustomAppRemoteContainer';
import { ErrorBoundary } from './ErrorBoundary';
import { NotFoundPage } from './NotFoundPage';

export default function CustomAppPage() {
  const { appName } = useParams<{ appName: string }>();
  const clientId = useClientId();
  // DP-4118: the registry may alias this route's app to another app's bundle
  // (`appConfig.remoteApp`). Hold the mount until the lookup has arrived.
  const { remoteAppFor, studioFor, nameFor, isReady } =
    useAppRegistry(clientId);

  // DP-5481: every failure on this route means the same thing to the user —
  // `/apps/{appName}` did not resolve to a page. `resolveAppConfig` accepts any
  // pattern-valid id, so an unregistered app gets a config whose remoteEntry
  // 404s and throws inside the boundary; naming that "custom app error" leaks
  // an implementation detail for what is a plain 404. The boundary still
  // reports the exception to telemetry/Bugsnag before rendering this.
  if (!appName || !APP_NAME_PATTERN.test(appName)) {
    return <NotFoundPage />;
  }

  if (!isReady) {
    return <LoadingSpinner />;
  }

  const resolvedConfig = resolveAppConfig(
    { name: appName, appName },
    appName,
    clientId,
    remoteAppFor(appName),
    studioFor(appName)
  );

  if (!resolvedConfig) {
    return <NotFoundPage />;
  }

  return (
    <ErrorBoundary fallback={<NotFoundPage />}>
      <CustomAppRemoteContainer
        config={resolvedConfig}
        clientId={clientId}
        // `entityName` stays the registry id: it is the app's address, not its
        // label — the remote uses it to resolve which registration it is
        // serving (Genie reads it as the agent id), and the route is keyed by
        // it. Only the *title* becomes the human name.
        entityName={appName}
        entityTitle={nameFor(appName) ?? appName}
        entityData={{}}
      />
    </ErrorBoundary>
  );
}
